import type { LumberPiecesByProfile, LumberProfileType } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { MIN_ROOF_FRAMING_SPACING } from "building-types/framing/roofFramingConstants";
import type {
  FramingPlaneConstraint,
  GeneratedRoofFramingPlane,
  RoofCriticalPoint,
  RoofRafter,
} from "building-types/framing/roofFramingTypes";
import {
  FramingPlaneConstraintType,
  RoofFramingPlaneType,
  RoofFramingSpacingMethod,
  RoofRegion,
} from "building-types/framing/roofFramingTypes";
import { RoofFramingType } from "building-types/framing/roofFramingTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { RWTable, Table } from "common/data/table";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew, lumberRailNew } from "doc/utils/lumberTools";
import { aabb3Overlaps } from "geom/aabb3";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { IntersectionKind, intersectLine2Ray2, intersectRay2PolyLine2 } from "geom/intersectRay2";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import { joinPolyLine3Rails } from "geom/join";
import { planeNew, planeNormal } from "geom/plane";
import { polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import type { DCEL, Polygon } from "geom/polyclip/dcel";
import { dcelFromPolygons } from "geom/polyclip/dcelFromPolygons";
import { offsetClosedPolyLine2 } from "geom/polyclip/offsetClosedPolyLine2";
import { polyLine2ToPolyClipPolygonLoop } from "geom/polyclip/polyClip";
import { setContains } from "geom/polyclip/setOps";
import { polygonSubtract, polygonUnion } from "geom/polygon";
import { polyLine2Area, polyLine2SetCW, polyLine2Simplify } from "geom/polyLine2";
import type { PolyLine2, PolyLine2Polygon } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3Move,
  polyLine3New,
  polyLine3Reverse,
  polyLine3Simplify,
} from "geom/polyLine3";
import { polyLine3ToPolyLine2IgnoreZ } from "geom/polyLine3";
import { vec2Dist, vec2Normalize, vec2Perp, type Vec2 } from "geom/vec2";
import { vec3Add, vec3Cross, vec3Dist, vec3Dot, vec3Normalize, vec3Sub } from "geom/vec3";
import { projectChordsToFramingPlane, projectPointToFramingPlane, setupJackChordRails } from "./trussUtils";

const RIDGE_RAFTER_CLIP_EXTENSION: UnsignedNumber = 6 as UnsignedNumber;

export function generateRidgeRafter(
  ridgeRafterPolyLine: PolyLine3,
  ridgeRafterProfile: LumberProfileType,
  clipPlanes?: Plane[],
  _addTopPlateClipPlanes: boolean = true,
  _leftPitch?: UnsignedNumber,
  _rightPitch?: UnsignedNumber,
): RoofRafter {
  const [railX, railY, railZ] = vec3Sub(
    ridgeRafterPolyLine[3],
    ridgeRafterPolyLine[4],
    ridgeRafterPolyLine[5],
    ridgeRafterPolyLine[0],
    ridgeRafterPolyLine[1],
    ridgeRafterPolyLine[2],
  );
  const [railNormX, railNormY, railNormZ] = vec3Normalize(railX, railY, railZ);
  const framingPlane: Plane = planeNew(
    ridgeRafterPolyLine[0],
    ridgeRafterPolyLine[1],
    ridgeRafterPolyLine[2],
    railNormX,
    railNormY,
    railNormZ,
    0,
    0,
    1,
  );
  const [inplaneX, inplaneY, inplaneZ] = vec3Cross(railNormX, railNormY, railNormZ, 0, 0, 1);
  const ridgeRafterProfileDescriptor = LumberProfileCatalog[ridgeRafterProfile];
  const ridgeRafter = lumberPiecesByProfileNew(ridgeRafterProfileDescriptor);
  const ridgeRafterHalfWidth = unsignedNumberNew(ridgeRafterProfileDescriptor.height / 2);
  const [moveX, moveY, moveZ] = [
    -inplaneX * ridgeRafterHalfWidth,
    -inplaneY * ridgeRafterHalfWidth,
    -inplaneZ * ridgeRafterHalfWidth,
  ];

  const { railsByType } = ridgeRafter;

  if (!railsByType[RoofFramingComponentType.RIDGE]) {
    railsByType[RoofFramingComponentType.RIDGE] = [];
  }

  const ridgeRafter3Copy = polyLine3New(ridgeRafterPolyLine);
  polyLine3Move(ridgeRafter3Copy, moveX, moveY, moveZ);

  const clipPlanesTop: Plane[] = []; //@TODO: Not a big deal as the ridge board is not poking out
  const ridgeClipPlanes: Plane[] = [];
  ridgeClipPlanes.push(...clipPlanesTop);
  if (clipPlanes) {
    ridgeClipPlanes.push(...clipPlanes);
  }

  railsByType[RoofFramingComponentType.RIDGE].push(
    lumberRailNew(
      ridgeRafter3Copy,
      inplaneX,
      inplaneY,
      inplaneZ,
      ridgeClipPlanes,
      RIDGE_RAFTER_CLIP_EXTENSION,
      RIDGE_RAFTER_CLIP_EXTENSION,
    ),
  );

  return {
    components: [{ type: RoofFramingComponentType.RIDGE, lumber: ridgeRafter }],
    framingType: RoofFramingType.RAFTER,
    plane: framingPlane,
  };
}

export function generateJackRafter(
  span: UnsignedNumber,
  pitch: UnsignedNumber,
  profile: LumberProfileType,
  framingPlane: Plane,
  fasciaSize: UnsignedNumber,
  isLeftJack: boolean,
): RoofRafter {
  const leftX1 = isLeftJack ? -span : span;
  const leftZ1 = -span * pitch;
  let spanChord = polyLine3New([
    leftX1,
    0,
    leftZ1, // Outer edge, setupTopChordRails
    0,
    0,
    0,
  ]);
  if (!isLeftJack) {
    spanChord = polyLine3Reverse(spanChord);
  }
  const [spanChordToFramingPlane] = projectChordsToFramingPlane([spanChord], framingPlane);

  const spanChords: LumberPiecesByProfile = lumberPiecesByProfileNew(LumberProfileCatalog[profile], { [profile]: [] });

  const [normX, normY, normZ] = planeNormal(framingPlane);

  setupJackChordRails(spanChords, spanChordToFramingPlane, fasciaSize, normX, normY, normZ);

  return {
    components: [{ type: RoofFramingComponentType.JACK, lumber: spanChords }],
    framingType: RoofFramingType.RAFTER,
    plane: framingPlane,
  };
}

export function createRidgeClipPlanes(
  origin: [number, number, number],
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  ridgeVector: [number, number, number],
): Plane[] {
  const [normX, normY, normZ] = vec3Normalize(ridgeVector[0], ridgeVector[1], ridgeVector[2]);

  const [uX, uY, uZ] = vec3Cross(normX, normY, normZ, 0, 0, 1);

  const originPlane = planeNew(origin[0], origin[1], origin[2], uX, uY, uZ, 0, 0, 1);

  const cosThetaLeft = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
  const sinThetaLeft = leftTopChordPitch * cosThetaLeft;

  const [leftDirX, leftDirY, leftDirZ] = projectPointToFramingPlane(-cosThetaLeft, 0, -sinThetaLeft, originPlane);
  const [leftDirNormX, leftDirNormY, leftDirNormZ] = vec3Normalize(
    origin[0] - leftDirX,
    origin[1] - leftDirY,
    origin[2] - leftDirZ,
  );

  const cosThetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
  const sinThetaRight = rightTopChordPitch * cosThetaRight;

  const [rightDirX, rightDirY, rightDirZ] = projectPointToFramingPlane(cosThetaRight, 0, -sinThetaRight, originPlane);
  const [rightDirNormX, rightDirNormY, rightDirNormZ] = vec3Normalize(
    rightDirX - origin[0],
    rightDirY - origin[1],
    rightDirZ - origin[2],
  );

  const leftClipPlane = planeNew(
    origin[0],
    origin[1],
    origin[2],
    leftDirNormX,
    leftDirNormY,
    leftDirNormZ,
    ridgeVector[0],
    ridgeVector[1],
    ridgeVector[2],
  );
  const rightClipPlane = planeNew(
    origin[0],
    origin[1],
    origin[2],
    rightDirNormX,
    rightDirNormY,
    rightDirNormZ,
    ridgeVector[0],
    ridgeVector[1],
    ridgeVector[2],
  );

  return [leftClipPlane, rightClipPlane];
}

export function generateLookout(
  leftSpan: UnsignedNumber,
  rightSpan: UnsignedNumber,
  leftOverhang: UnsignedNumber,
  rightOverhang: UnsignedNumber,
  leftTopChordPitch: UnsignedNumber,
  rightTopChordPitch: UnsignedNumber,
  lookoutLength: UnsignedNumber,
  lookoutSpacing: UnsignedNumber,
  lookoutProfile: LumberProfileType,
  originPlane: Plane,
): RoofRafter {
  const lookoutProfileDescriptor = LumberProfileCatalog[lookoutProfile];
  const lookout = lumberPiecesByProfileNew(lookoutProfileDescriptor);
  const lookoutHalfWidth = unsignedNumberNew(lookoutProfileDescriptor.height / 2);
  const [normX, normY, normZ] = planeNormal(originPlane);

  const { railsByType } = lookout;

  if (!railsByType[RoofFramingComponentType.LOOKOUT]) {
    railsByType[RoofFramingComponentType.LOOKOUT] = [];
  }

  const cosThetaLeft = 1 / Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
  const sinThetaLeft = leftTopChordPitch * cosThetaLeft;

  const [leftDirX, leftDirY, leftDirZ] = projectPointToFramingPlane(-cosThetaLeft, 0, -sinThetaLeft, originPlane);
  const [leftDirNormX, leftDirNormY, leftDirNormZ] = vec3Normalize(
    originPlane.originX - leftDirX,
    originPlane.originY - leftDirY,
    originPlane.originZ - leftDirZ,
  );

  const cosThetaRight = 1 / Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
  const sinThetaRight = rightTopChordPitch * cosThetaRight;

  const [rightDirX, rightDirY, rightDirZ] = projectPointToFramingPlane(cosThetaRight, 0, -sinThetaRight, originPlane);
  const [rightDirNormX, rightDirNormY, rightDirNormZ] = vec3Normalize(
    rightDirX - originPlane.originX,
    rightDirY - originPlane.originY,
    rightDirZ - originPlane.originZ,
  );

  const leftRafterLengthWithoutOverhang = (leftSpan - leftOverhang) / cosThetaLeft;
  let startLookOutPos = lookoutSpacing + lookoutHalfWidth;

  while (startLookOutPos < leftRafterLengthWithoutOverhang) {
    const [lookoutStartX, lookoutStartY, lookoutStartZ] = projectPointToFramingPlane(
      -startLookOutPos * cosThetaLeft,
      0,
      -startLookOutPos * sinThetaLeft,
      originPlane,
    );
    const [lookoutEndX, lookoutEndY, lookoutEndZ] = vec3Add(
      lookoutStartX,
      lookoutStartY,
      lookoutStartZ,
      -normX * lookoutLength,
      -normY * lookoutLength,
      -normZ * lookoutLength,
    );
    const lookoutPolyLine = polyLine3New([
      lookoutStartX,
      lookoutStartY,
      lookoutStartZ,
      lookoutEndX,
      lookoutEndY,
      lookoutEndZ,
    ]);
    const lookoutRail = lumberRailNew(lookoutPolyLine, leftDirNormX, leftDirNormY, leftDirNormZ);
    railsByType[RoofFramingComponentType.LOOKOUT].push(lookoutRail);
    startLookOutPos += lookoutSpacing + lookoutHalfWidth;
  }
  const rightRafterLengthWithoutOverhang = (rightSpan - rightOverhang) / cosThetaRight;
  startLookOutPos = lookoutSpacing - lookoutHalfWidth;
  while (startLookOutPos < rightRafterLengthWithoutOverhang) {
    const [lookoutStartX, lookoutStartY, lookoutStartZ] = projectPointToFramingPlane(
      startLookOutPos * cosThetaRight,
      0,
      -startLookOutPos * sinThetaRight,
      originPlane,
    );
    const [lookoutEndX, lookoutEndY, lookoutEndZ] = vec3Add(
      lookoutStartX,
      lookoutStartY,
      lookoutStartZ,
      -normX * lookoutLength,
      -normY * lookoutLength,
      -normZ * lookoutLength,
    );
    const lookoutPolyLine = polyLine3New([
      lookoutStartX,
      lookoutStartY,
      lookoutStartZ,
      lookoutEndX,
      lookoutEndY,
      lookoutEndZ,
    ]);
    const lookoutRail = lumberRailNew(lookoutPolyLine, rightDirNormX, rightDirNormY, rightDirNormZ);
    railsByType[RoofFramingComponentType.LOOKOUT].push(lookoutRail);
    startLookOutPos += lookoutSpacing + lookoutHalfWidth;
  }

  return {
    components: [{ type: RoofFramingComponentType.LOOKOUT, lumber: lookout }],
    framingType: RoofFramingType.RAFTER,
    plane: originPlane, //@TODO: needs a fix//this is NOT used anywhere but this plane should be the plane of the rafter. this is not the case but when we
    //will have the rafters drawn in plan view, this will be the plane of the rafter
  };
}

export function roofFramingPlanes(
  peakRail: PolyLine3,
  peakRailFull: PolyLine3,
  startOffset: number,
  endOffset: number,
  roofFramingSpacing: UnsignedNumber,
  roofFramingSpacingMethod: RoofFramingSpacingMethod = RoofFramingSpacingMethod.EQUAL_SPACING_BEGIN_END,
  constraints: FramingPlaneConstraint[] = [],
): GeneratedRoofFramingPlane[] {
  const { roofCriticalPointsAndRegion, peakRailUnitVector } = calculateRoofCriticalPoints(
    peakRail,
    peakRailFull,
    startOffset,
    endOffset,
    constraints,
  );
  const [peakRailFullNormX, peakRailFullNormY, peakRailFullNormZ] = peakRailUnitVector;
  // Calculate cross product for plane orientation (using first rail direction)
  const [inplaneX, inplaneY, inplaneZ] = vec3Cross(peakRailFullNormX, peakRailFullNormY, peakRailFullNormZ, 0, 0, 1);
  const criticalPointsArray = roofCriticalPointsAndRegion.reduce<number[]>(
    (acc, point) => [...acc, point.x, point.y, point.z],
    [],
  );
  const rafterLine = polyLine3New([...criticalPointsArray]);
  let regions = roofCriticalPointsAndRegion.map((point) => point.region);

  let result: PolyLine3 | undefined = undefined;
  let polyLineAdjusted: PolyLine3 = polyLine3Copy(rafterLine);
  let roofRegion: (RoofRegion | "NONE")[] = [];
  const reversed = roofFramingSpacingMethod === RoofFramingSpacingMethod.EQUAL_SPACING_END;
  if (reversed) {
    polyLineAdjusted = polyLine3Reverse(polyLineAdjusted);
    regions = regions.reverse();
  }

  const generatedRoofFramingPlanes: GeneratedRoofFramingPlane[] = [];
  let i = 0;
  const edgeCount = polyLine3EdgeCount(polyLineAdjusted);
  const spacingTolToSkipFirstLastPoint = 0.75 * MIN_ROOF_FRAMING_SPACING;

  for (i = 0; i < edgeCount; i++) {
    const edge = polyLine3Edge(polyLineAdjusted, i);
    const x1 = edge[0];
    const y1 = edge[1];
    const z1 = edge[2];
    const x2 = edge[3];
    const y2 = edge[4];
    const z2 = edge[5];
    const [dx, dy, dz] = vec3Sub(x2, y2, z2, x1, y1, z1);
    const [unitX, unitY, unitZ] = vec3Normalize(dx, dy, dz);
    const edgeLength = vec3Dist(x1, y1, z1, x2, y2, z2);
    result = result ? polyLine3New([...result, x1, y1, z1]) : polyLine3New([x1, y1, z1]);
    roofRegion.push(regions[i]);

    let computedStartRafterOffset: number = roofFramingSpacing;
    if (roofFramingSpacingMethod === RoofFramingSpacingMethod.EQUAL_SPACING_BEGIN_END) {
      const subDiv = Math.floor(edgeLength / roofFramingSpacing);
      const halfRemainder = 0.5 * (edgeLength - subDiv * roofFramingSpacing);
      if (halfRemainder < spacingTolToSkipFirstLastPoint) {
        //too close to the end, skip the first point and start from half remainder + spacing
        computedStartRafterOffset += halfRemainder; //skip the first point
      } else {
        computedStartRafterOffset = halfRemainder; //split the remainder in half, start from half remainder so that last point is at the half remainder
      }
    }
    let posL = computedStartRafterOffset;
    while (posL < edgeLength) {
      const [x, y, z] = vec3Add(x1, y1, z1, unitX * posL, unitY * posL, unitZ * posL);
      result = result ? polyLine3New([...result, x, y, z]) : polyLine3New([x, y, z]);
      reversed ? roofRegion.push(regions[i + 1]) : roofRegion.push(regions[i]);
      posL += roofFramingSpacing;
      if (roofFramingSpacingMethod === RoofFramingSpacingMethod.EQUAL_SPACING_BEGIN_END) {
        if (posL > edgeLength - computedStartRafterOffset) {
          break;
        }
      } else {
        if (posL > edgeLength - spacingTolToSkipFirstLastPoint) {
          break;
        }
      }
    }
  }
  if (result) {
    const lastEdge = polyLine3Edge(polyLineAdjusted, edgeCount - 1);
    result = polyLine3New([...result, lastEdge[3], lastEdge[4], lastEdge[5]]);
    roofRegion.push(regions[i]);

    if (reversed) {
      result = polyLine3Reverse(result);
      roofRegion = roofRegion.reverse();
    }

    for (let i = 0; i < roofRegion.length; i++) {
      const plane = planeNew(
        result[i * 3],
        result[i * 3 + 1],
        result[i * 3 + 2],
        inplaneX,
        inplaneY,
        inplaneZ,
        0,
        0,
        1,
      );
      const generatedRoofFramingPlane: GeneratedRoofFramingPlane = {
        plane: plane,
        type: getRoofFramingPlaneType(roofRegion, i),
      };
      generatedRoofFramingPlanes.push(generatedRoofFramingPlane);
    }
  }
  return generatedRoofFramingPlanes;
}

function getRoofFramingPlaneType(region: (RoofRegion | "NONE")[], index: number): RoofFramingPlaneType {
  const isFirstPeak =
    index > 0 &&
    region[index - 1] !== RoofRegion.PEAK &&
    region[index] === RoofRegion.PEAK &&
    region[index - 1] !== RoofRegion.HIP;
  const isLastPeak =
    index < region.length - 1 &&
    region[index - 1] === RoofRegion.PEAK &&
    region[index] !== RoofRegion.PEAK &&
    region[index] !== RoofRegion.HIP;

  const isEndStartHipRegion =
    index < region.length - 1 && region[index] === RoofRegion.HIP && region[index - 1] === RoofRegion.PEAK;
  const isEndHipRegion = index === region.length - 1 && region[index - 1] === RoofRegion.HIP;

  switch (region[index]) {
    case RoofRegion.HIP:
      if (isEndStartHipRegion) {
        return RoofFramingPlaneType.PEAK_FRAMING_PLANE;
      } else {
        return RoofFramingPlaneType.HIP_FRAMING_PLANE;
      }
    case RoofRegion.FLYOUT:
      if (index === 0) {
        return RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_START;
      } else if (!isLastPeak) {
        return RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_INTERIM;
      } else if (isLastPeak) {
        return RoofFramingPlaneType.STICK_FRAME_PLANE_AT_END;
      }
    case RoofRegion.PEAK:
      if (isFirstPeak) {
        return RoofFramingPlaneType.STICK_FRAME_PLANE_AT_START;
      } else if (isLastPeak) {
        return RoofFramingPlaneType.STICK_FRAME_PLANE_AT_END;
      } else {
        return RoofFramingPlaneType.PEAK_FRAMING_PLANE;
      }
    case "NONE":
      return isEndHipRegion ? RoofFramingPlaneType.HIP_FRAMING_PLANE : RoofFramingPlaneType.FLY_RAFTER_PLANE_AT_END;
  }
}

const IGNORE_CONSTRAINT_DISTANCE = 1.5;

export function calculateRoofCriticalPoints(
  peakRail: PolyLine3,
  peakRailFull: PolyLine3,
  startOffset: number,
  endOffset: number,
  constraints: FramingPlaneConstraint[] = [],
): {
  readonly roofCriticalPointsAndRegion: RoofCriticalPoint[];
  readonly peakRailUnitVector: [number, number, number];
} {
  // Calculate direction vector from the first rail for consistent distance calculations
  const [peakRailDirX, peakRailDirY, peakRailDirZ] = vec3Sub(
    peakRail[3],
    peakRail[4],
    peakRail[5],
    peakRail[0],
    peakRail[1],
    peakRail[2],
  );
  const [peakRailFullDirX, peakRailFullDirY, peakRailFullDirZ] = vec3Sub(
    peakRailFull[3],
    peakRailFull[4],
    peakRailFull[5],
    peakRailFull[0],
    peakRailFull[1],
    peakRailFull[2],
  );
  const [peakRailFullNormX, peakRailFullNormY, peakRailFullNormZ] = vec3Normalize(
    peakRailFullDirX,
    peakRailFullDirY,
    peakRailFullDirZ,
  );

  const dotProdPeakRails: number = vec3Dot(
    peakRailDirX,
    peakRailDirY,
    peakRailDirZ,
    peakRailFullDirX,
    peakRailFullDirY,
    peakRailFullDirZ,
  );

  let peakRailAdjusted = polyLine3Copy(peakRail);

  if (dotProdPeakRails < 0) {
    //reverse the peak rail
    peakRailAdjusted = polyLine3Reverse(peakRailAdjusted);
  }

  const roofCriticalPointsAndRegion: RoofCriticalPoint[] = [];

  const distanceStartRails = vec3Dist(
    peakRailAdjusted[0],
    peakRailAdjusted[1],
    peakRailAdjusted[2],
    peakRailFull[0],
    peakRailFull[1],
    peakRailFull[2],
  );
  const distanceEndRails = vec3Dist(
    peakRailAdjusted[3],
    peakRailAdjusted[4],
    peakRailAdjusted[5],
    peakRailFull[3],
    peakRailFull[4],
    peakRailFull[5],
  );

  roofCriticalPointsAndRegion.push({
    x: peakRailFull[0],
    y: peakRailFull[1],
    z: peakRailFull[2],
    distance: 0,
    region: distanceStartRails > DIST_EPSILON ? RoofRegion.HIP : RoofRegion.FLYOUT,
  });

  if (distanceStartRails > DIST_EPSILON) {
    roofCriticalPointsAndRegion.push({
      x: peakRailAdjusted[0],
      y: peakRailAdjusted[1],
      z: peakRailAdjusted[2],
      distance: distanceStartRails,
      region: RoofRegion.PEAK,
    });
  } else {
    const extendedStart = polyLine3ExtendStart(polyLine3Copy(peakRailAdjusted), startOffset);
    const distanceStart = vec3Dist(
      roofCriticalPointsAndRegion[0].x,
      roofCriticalPointsAndRegion[0].y,
      roofCriticalPointsAndRegion[0].z,
      extendedStart[0],
      extendedStart[1],
      extendedStart[2],
    );
    roofCriticalPointsAndRegion.push({
      x: extendedStart[0],
      y: extendedStart[1],
      z: extendedStart[2],
      distance: distanceStart,
      region: RoofRegion.PEAK,
    });
  }

  if (distanceEndRails > DIST_EPSILON) {
    const distanceEndFromStart = vec3Dist(
      roofCriticalPointsAndRegion[0].x,
      roofCriticalPointsAndRegion[0].y,
      roofCriticalPointsAndRegion[0].z,
      peakRailAdjusted[3],
      peakRailAdjusted[4],
      peakRailAdjusted[5],
    );
    roofCriticalPointsAndRegion.push({
      x: peakRailAdjusted[3],
      y: peakRailAdjusted[4],
      z: peakRailAdjusted[5],
      distance: distanceEndFromStart,
      region: RoofRegion.HIP,
    });
  } else {
    const extendedEnd = polyLine3ExtendEnd(polyLine3Copy(peakRailAdjusted), endOffset);
    const distanceEndFromStart = vec3Dist(
      roofCriticalPointsAndRegion[0].x,
      roofCriticalPointsAndRegion[0].y,
      roofCriticalPointsAndRegion[0].z,
      extendedEnd[3],
      extendedEnd[4],
      extendedEnd[5],
    );
    roofCriticalPointsAndRegion.push({
      x: extendedEnd[3],
      y: extendedEnd[4],
      z: extendedEnd[5],
      distance: distanceEndFromStart,
      region: RoofRegion.FLYOUT,
    });
  }
  const lengthPeakRail = vec3Dist(
    peakRailFull[0],
    peakRailFull[1],
    peakRailFull[2],
    peakRailFull[3],
    peakRailFull[4],
    peakRailFull[5],
  );

  roofCriticalPointsAndRegion.push({
    x: peakRailFull[3],
    y: peakRailFull[4],
    z: peakRailFull[5],
    distance: lengthPeakRail,
    region: "NONE",
  });

  const roofCriticalPointsAndRegionWithConstraints: RoofCriticalPoint[] = [];

  let startConstraintIndex = 0;
  for (let i = 0; i < roofCriticalPointsAndRegion.length - 1; i++) {
    const { x, y, z, region } = roofCriticalPointsAndRegion[i];
    roofCriticalPointsAndRegionWithConstraints.push(roofCriticalPointsAndRegion[i]);
    const distance1 = roofCriticalPointsAndRegion[i].distance;
    const distance2 = roofCriticalPointsAndRegion[i + 1].distance;
    for (let j = startConstraintIndex; j < constraints.length; j++) {
      if (constraints[j].type === FramingPlaneConstraintType.NO_FRAMING) {
        //we don't want to add constraints that are no framing, we will handle this in the next step
        continue;
      }
      const distanceFromStart = constraints[j].distanceFromStart;
      if (
        distanceFromStart > distance1 + IGNORE_CONSTRAINT_DISTANCE &&
        distanceFromStart < distance2 - IGNORE_CONSTRAINT_DISTANCE
      ) {
        roofCriticalPointsAndRegionWithConstraints.push({
          x: x + (distanceFromStart - distance1) * peakRailFullNormX,
          y: y + (distanceFromStart - distance1) * peakRailFullNormY,
          z: z + (distanceFromStart - distance1) * peakRailFullNormZ,
          distance: distanceFromStart,
          region: region,
        });
      }
      if (distanceFromStart >= distance2 - DIST_EPSILON) {
        startConstraintIndex = j;
        break;
      }
    }
  }
  roofCriticalPointsAndRegionWithConstraints.push(roofCriticalPointsAndRegion[roofCriticalPointsAndRegion.length - 1]);

  return {
    roofCriticalPointsAndRegion: roofCriticalPointsAndRegionWithConstraints,
    peakRailUnitVector: [peakRailFullNormX, peakRailFullNormY, peakRailFullNormZ],
  };
}

const OFFSET_ROOF_TOP_FACES = 1;
const MIN_AREA_ROOF_TOP_FACES = 1;
export function roofTopFacesUnionXYPlane(roof: Pick<GeneratedRoof, "roofTopFaces">): PolyLine2[] {
  const { roofTopFaces } = roof;
  const roofTopFacesXY: PolyLine2[][] = [];
  for (const { face } of roofTopFaces) {
    const { plane, loops } = face;
    for (const loop of loops) {
      const polyline3 = polyLine2OnPlane(plane, loop);
      const polyLine2XY = polyLine2SetCW(polyLine2Simplify(polyLine3ToPolyLine2IgnoreZ(polyline3)));
      roofTopFacesXY.push(...offsetClosedPolyLine2([polyLine2XY], OFFSET_ROOF_TOP_FACES));
    }
  }
  const unionedRoofTopFacesXY: PolyLine2[] = polygonUnion(roofTopFacesXY).flat();
  const filteredRoofTopFacesXY: PolyLine2[] = [];

  for (const pl of unionedRoofTopFacesXY) {
    const offsetPls = offsetClosedPolyLine2([polyLine2SetCW(polyLine2Simplify(pl))], -OFFSET_ROOF_TOP_FACES);
    for (const pls of offsetPls) {
      const plsFiltered = pls
        .filter((pl) => polyLine2Area(pl) > MIN_AREA_ROOF_TOP_FACES)
        .map((pl) => polyLine2Simplify(pl));
      filteredRoofTopFacesXY.push(...plsFiltered);
    }
  }
  return filteredRoofTopFacesXY;
}

export function roofFramingSpanBuilder(roofTopFacesXYPlane: PolyLine2[]): (x: number, y: number, vec: Vec2) => number {
  function computeSpanAt(x: number, y: number, vec: Vec2): number {
    let span: number = Number.MAX_VALUE;
    for (const faceLoop of roofTopFacesXYPlane) {
      const ints = intersectRay2PolyLine2(faceLoop, x, y, vec[0], vec[1]);
      for (const int of ints) {
        const { kind } = int;
        if (kind === IntersectionKind.TRANSVERSE) {
          const { t } = int;
          if (t < span) {
            span = t;
          }
        }
      }
    }
    return span;
  }
  return computeSpanAt;
}

export function roofFramingConstraintPoints(
  peakRidgeRail: PolyLine3,
  roofTopFacesXYPlane: PolyLine2[],
  minDistanceBetweenConstraints: number = 6,
): FramingPlaneConstraint[] {
  const framingPlaneConstraints: FramingPlaneConstraint[] = [];
  const peakRidgeRailXY = polyLine3ToPolyLine2IgnoreZ(peakRidgeRail);
  const [vecPerpX, vecPerpY] = vec2Perp(
    peakRidgeRailXY[2] - peakRidgeRailXY[0],
    peakRidgeRailXY[3] - peakRidgeRailXY[1],
  );
  const [vecPerpNormX, vecPerpNormY] = vec2Normalize(vecPerpX, vecPerpY);
  const length = vec2Dist(peakRidgeRailXY[0], peakRidgeRailXY[1], peakRidgeRailXY[2], peakRidgeRailXY[3]);

  for (const pl of roofTopFacesXYPlane) {
    for (let i = 0, l = pl.length; i < l; i += 2) {
      const rayStartX = pl[i];
      const rayStartY = pl[i + 1];
      const ints = intersectLine2Ray2(
        peakRidgeRailXY[0],
        peakRidgeRailXY[1],
        peakRidgeRailXY[2],
        peakRidgeRailXY[3],
        rayStartX,
        rayStartY,
        vecPerpNormX,
        vecPerpNormY,
      );
      if (!ints) {
        continue;
      }
      const { x, y } = ints;
      const distanceFromStart = vec2Dist(peakRidgeRailXY[0], peakRidgeRailXY[1], x, y);
      if (
        distanceFromStart > minDistanceBetweenConstraints &&
        distanceFromStart < length - minDistanceBetweenConstraints
      ) {
        let isConstraint = true;
        for (let i = 0; i < framingPlaneConstraints.length; i++) {
          const { distanceFromStart: distance } = framingPlaneConstraints[i];
          if (Math.abs(distanceFromStart - distance) < minDistanceBetweenConstraints) {
            isConstraint = false;
            break;
          }
        }
        if (isConstraint) {
          framingPlaneConstraints.push({ type: FramingPlaneConstraintType.FRAMING, distanceFromStart });
        }
      }
    }
  }
  return framingPlaneConstraints.sort((a, b) => a.distanceFromStart - b.distanceFromStart);
}

function roofTopFacesXYPlaneClipped(
  subRoofId: string,
  roofTopFacesUnionedXYPlanes: Table<PolyLine2[]>,
  roofNeighbors: Table<string[]>,
): PolyLine2[] {
  const unionedMainRoofTopFaces = roofTopFacesUnionedXYPlanes[subRoofId];
  const neighborIds = roofNeighbors[subRoofId];
  //if there are no neighbors or no unioned main roof top faces, return the unioned main roof top faces or an empty array
  if (!neighborIds || !unionedMainRoofTopFaces) {
    return unionedMainRoofTopFaces ?? [];
  }
  //get the unioned roof top faces of the neighbors
  const unionedNeighborRoofTopFacesToSubtract: PolyLine2Polygon[] = [];
  for (const neighborId of neighborIds) {
    const unionedNeighborRoofTopFaces = roofTopFacesUnionedXYPlanes[neighborId];
    unionedNeighborRoofTopFacesToSubtract.push(unionedNeighborRoofTopFaces);
  }
  //subtract the unioned roof top faces of the neighbors from the unioned main roof top faces
  const intersection: PolyLine2Polygon[] = [];

  unionedMainRoofTopFaces.forEach((pl) => {
    const subtracted = polygonSubtract([[pl], ...unionedNeighborRoofTopFacesToSubtract]);
    intersection.push(...subtracted);
  });
  //return the intersection as a flat array of polygons
  return intersection
    .flat()
    .filter((pl) => polyLine2Area(pl) > MIN_AREA_ROOF_TOP_FACES)
    .map((pl) => polyLine2Simplify(pl));
}

const ONE_INCH = 1;

export function roofTopFacesXYPlaneAndNeighbors(
  roofs: Table<Pick<GeneratedRoof, "roofTopFaces" | "roofVolume" | "id">>,
): {
  roofTopFacesUnionedXYPlanes: Table<PolyLine2[]>;
  roofNeighborsIds: Table<string[]>;
  roofTopFacesDCEL: Table<DCEL>;
  polygonRoofIdMapping: Table<string[]>;
} {
  const roofTopFacesUnionedXYPlanes: RWTable<PolyLine2[]> = {};
  const roofNeighborsIds: RWTable<string[]> = {};
  const roofTopFacesDCEL: RWTable<DCEL> = {};
  const polygonRoofIdMapping: RWTable<string[]> = {};
  for (const roofId in roofs) {
    const roof = roofs[roofId];
    if (!roofTopFacesUnionedXYPlanes[roofId]) {
      roofTopFacesUnionedXYPlanes[roofId] = roofTopFacesUnionXYPlane(roof);
    }
  }
  const roofsArray = Object.values(roofs);
  for (let i = 0; i < roofsArray.length; i++) {
    const { aabb3: abb3MainRoof } = roofsArray[i].roofVolume;
    const roofId = roofsArray[i].id;
    for (let j = 0; j < roofsArray.length; j++) {
      if (i === j) {
        continue;
      }
      const { aabb3: abb3NeighborRoof } = roofsArray[j].roofVolume;
      const neighborId = roofsArray[j].id;
      if (aabb3Overlaps(abb3MainRoof, abb3NeighborRoof) && !roofNeighborsIds[roofId]?.includes(neighborId)) {
        if (!roofNeighborsIds[roofId]) {
          roofNeighborsIds[roofId] = [];
        }
        roofNeighborsIds[roofId].push(neighborId);
      }
    }
  }
  for (const roofId in roofs) {
    if (roofTopFacesUnionedXYPlanes[roofId]) {
      roofTopFacesUnionedXYPlanes[roofId] = roofTopFacesXYPlaneClipped(
        roofId,
        roofTopFacesUnionedXYPlanes,
        roofNeighborsIds,
      );
    }
  }

  for (const roofId in roofs) {
    const polygons: Polygon[] = [];
    const roofIdToPolygonId: string[] = [];

    const topFacesPolyLines = roofTopFacesUnionedXYPlanes[roofId];
    if (!topFacesPolyLines) {
      continue;
    }

    topFacesPolyLines.forEach((pl) => {
      polygons.push([polyLine2ToPolyClipPolygonLoop(pl, false, true, WindingDir.CW)]);
      roofIdToPolygonId.push(roofId);
    });

    if (roofNeighborsIds[roofId]) {
      for (const roofNeighborId of roofNeighborsIds[roofId]) {
        const topFacesPolyLines = roofTopFacesUnionedXYPlanes[roofNeighborId];
        if (!topFacesPolyLines) {
          continue;
        }
        topFacesPolyLines.forEach((pl) => {
          polygons.push([polyLine2ToPolyClipPolygonLoop(pl, false, true, WindingDir.CW)]);
          roofIdToPolygonId.push(roofNeighborId);
        });
      }
    }
    roofTopFacesDCEL[roofId] = dcelFromPolygons({
      polygons,
      snapRoundPrecision: 0.001,
    });
    polygonRoofIdMapping[roofId] = roofIdToPolygonId;
  }
  return { roofTopFacesUnionedXYPlanes, roofNeighborsIds, roofTopFacesDCEL, polygonRoofIdMapping };
}

export function roofFramingPeakRidgeControlLines(
  roofTopFacesXYPlanes: PolyLine2[],
  peakRidgeLine: PolyLine3,
): PolyLine3[] {
  const controlLines: PolyLine3[] = [];
  const [dirX, dirY] = vec2Normalize(peakRidgeLine[3] - peakRidgeLine[0], peakRidgeLine[4] - peakRidgeLine[1]);
  const ridgeZ: number = peakRidgeLine[5];
  for (const roofTopFacesXYPlane of roofTopFacesXYPlanes) {
    const ints = intersectRay2PolyLine2(roofTopFacesXYPlane, peakRidgeLine[0], peakRidgeLine[1], dirX, dirY);
    if (!ints) {
      continue;
    }
    ints.sort((a, b) => a.t - b.t);
    const collectPointsAlongControlLine: number[] = [];
    for (const int of ints) {
      if (int.kind === IntersectionKind.TRANSVERSE) {
        const { t } = int;
        collectPointsAlongControlLine.push(...[peakRidgeLine[0] + t * dirX, peakRidgeLine[1] + t * dirY, ridgeZ]);
      }
    }
    if (collectPointsAlongControlLine.length > 0) {
      const controlLine = polyLine3Simplify(polyLine3New([...collectPointsAlongControlLine]));
      controlLines.push(controlLine);
    }
  }
  return controlLines;
}

export function roofFramingValleyControlLines(
  mainRoofId: string,
  mainRoofDCEL: DCEL | undefined,
  roofIdsMappingForMainRoof: string[] | undefined,
  subRoofs: Table<GeneratedRoof>,
) {
  const valleyControlLines: PolyLine3[] = [];
  if (!mainRoofDCEL || !roofIdsMappingForMainRoof) {
    console.warn(`Roof ${mainRoofId} has no DCEL or polygonRoofIdMapping`);
    return valleyControlLines;
  }
  const { vertices } = mainRoofDCEL;
  const roofFaceIndices: number[] = roofIdsMappingForMainRoof.map((_, index) => index);

  const interiorHalfEdgePairs = mainRoofDCEL.buildInteriorHalfEdgePairs();
  for (const [halfEdgeIndex, neighborHalfEdgeIndex] of interiorHalfEdgePairs) {
    const roofFaceId = mainRoofDCEL.halfEdgeToFace[halfEdgeIndex];
    const roofFace = mainRoofDCEL.faceToPolygons[roofFaceId];
    if (!setContains(roofFace, roofFaceIndices)) {
      continue;
    }
    const neighborRoofFaceId = mainRoofDCEL.halfEdgeToFace[neighborHalfEdgeIndex];
    const neighborRoofFace = mainRoofDCEL.faceToPolygons[neighborRoofFaceId];
    if (!setContains(neighborRoofFace, roofFaceIndices)) {
      continue;
    }
    const startVertex = vertices[mainRoofDCEL.halfEdges[halfEdgeIndex][0]];
    const endVertex = vertices[mainRoofDCEL.halfEdges[halfEdgeIndex][1]];
    let intersectZMinStartVertexMainRoof: number = Number.MAX_VALUE;
    let intersectZMinEndVertexMainRoof: number = Number.MAX_VALUE;

    //iterate on mainRoofFace set
    for (const face of roofFace) {
      const roofId = roofIdsMappingForMainRoof[face];
      const { roofTopFaces } = subRoofs[roofId];
      let valleyFound: boolean = false;
      for (const roofTopFace of roofTopFaces) {
        const { face: roofTopFaceFace } = roofTopFace;
        const { plane } = roofTopFaceFace;

        const [intersectZStartVertexMainRoof, intersectZEndVertexMainRoof] = vertexZs(startVertex, endVertex, plane);
        if (intersectZStartVertexMainRoof) {
          intersectZMinStartVertexMainRoof = Math.min(intersectZMinStartVertexMainRoof, intersectZStartVertexMainRoof);
        }
        if (intersectZEndVertexMainRoof) {
          intersectZMinEndVertexMainRoof = Math.min(intersectZMinEndVertexMainRoof, intersectZEndVertexMainRoof);
        }
      }
      let intersectZMinStartVertexIntersectingRoof: number = Number.MAX_VALUE;
      let intersectZMinEndVertexIntersectingRoof: number = Number.MAX_VALUE;
      if (
        intersectZMinStartVertexMainRoof !== Number.MAX_VALUE &&
        intersectZMinEndVertexMainRoof !== Number.MAX_VALUE
      ) {
        for (const intersectingRoofFace of neighborRoofFace) {
          const neighborRoofId = roofIdsMappingForMainRoof[intersectingRoofFace];
          const { roofTopFaces: neighborRoofTopFaces } = subRoofs[neighborRoofId];
          for (const neighborRoofTopFace of neighborRoofTopFaces) {
            const { face: neighborRoofTopFaceFace } = neighborRoofTopFace;
            const { plane } = neighborRoofTopFaceFace;
            const [intersectZStartVertexIntersectingRoof, intersectZEndVertexIntersectingRoof] = vertexZs(
              startVertex,
              endVertex,
              plane,
            );
            if (intersectZStartVertexIntersectingRoof) {
              intersectZMinStartVertexIntersectingRoof = Math.min(
                intersectZMinStartVertexIntersectingRoof,
                intersectZStartVertexIntersectingRoof,
              );
            }
            if (intersectZEndVertexIntersectingRoof) {
              intersectZMinEndVertexIntersectingRoof = Math.min(
                intersectZMinEndVertexIntersectingRoof,
                intersectZEndVertexIntersectingRoof,
              );
            }
          }
        }
        if (
          intersectZMinStartVertexIntersectingRoof !== Number.MAX_VALUE &&
          intersectZMinEndVertexIntersectingRoof !== Number.MAX_VALUE &&
          Math.abs(intersectZMinStartVertexIntersectingRoof - intersectZMinStartVertexMainRoof) < ONE_INCH &&
          Math.abs(intersectZMinEndVertexIntersectingRoof - intersectZMinEndVertexMainRoof) < ONE_INCH &&
          Math.abs(intersectZMinStartVertexMainRoof - intersectZMinEndVertexMainRoof) > ONE_INCH
        ) {
          const controlLine = polyLine3New([
            startVertex[0],
            startVertex[1],
            intersectZMinStartVertexMainRoof,
            endVertex[0],
            endVertex[1],
            intersectZMinEndVertexMainRoof,
          ]);
          valleyControlLines.push(
            intersectZMinStartVertexMainRoof < intersectZMinEndVertexMainRoof
              ? controlLine
              : polyLine3Reverse(controlLine),
          );
          valleyFound = true;
          break;
        }
      }
      if (valleyFound) {
        break;
      }
    }
  }
  const joinedValleys = joinPolyLine3Rails({
    segments: valleyControlLines,
    searchBoxTolerance: ONE_INCH,
    simplify: true,
    allowRevert: true,
  });
  return joinedValleys.filter((line) => polyLine3Length(line) > ONE_INCH);
}

//use huge negative number to make sure we are not obstructed by -negative or zero Z values of the roof volumes
const HUGE_NEGATIVE_NUMBER = -1000000;

function vertexZs(startVertex: Vec2, endVertex: Vec2, plane: Plane): [number | undefined, number | undefined] {
  const [normX, normY, normZ] = planeNormal(plane);
  const intersectStartVertex = intersectRay3PlaneWithNormal(
    startVertex[0],
    startVertex[1],
    HUGE_NEGATIVE_NUMBER,
    0,
    0,
    1,
    plane.originX,
    plane.originY,
    plane.originZ,
    normX,
    normY,
    normZ,
  );

  const intersectStartVertexZ = intersectStartVertex?.[2];

  const intersectEndVertex = intersectRay3PlaneWithNormal(
    endVertex[0],
    endVertex[1],
    HUGE_NEGATIVE_NUMBER,
    0,
    0,
    1,
    plane.originX,
    plane.originY,
    plane.originZ,
    normX,
    normY,
    normZ,
  );

  const intersectEndVertexZ = intersectEndVertex?.[2];

  return [intersectStartVertexZ, intersectEndVertexZ];
}
